import { useMemo } from 'react'

import { getDaysRemainingAt, parseStableDateInput } from '@/helpers/ssr/dates'

import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'

interface DaysRemainingResult {
  /** Number of days remaining (negative if past deadline) */
  daysRemaining: number | null
  /**
   * Whether the deadline has passed using the same whole-day rounding semantics
   * as daysRemaining, so "0 days left" stays active until the next full day.
   */
  isPast: boolean
  /** Parsed Date object (null if no date) */
  date: Date | null
}

/**
 * Calculate days remaining until a deadline date.
 *
 * Note: this hook owns its own 60s render clock. Avoid using it directly in
 * list items; for list UIs, derive the value from a shared parent
 * `referenceNowMs` instead.
 *
 * @param dateString - ISO date string or null/undefined
 * @returns Object with daysRemaining, isPast flag, and parsed Date
 *
 * @example
 * ```tsx
 * const { daysRemaining, isPast, date } = useDaysRemaining(notice.close_date)
 *
 * return (
 *   <div>
 *     {isPast ? 'Deadline passed' : `${daysRemaining} days left`}
 *   </div>
 * )
 * ```
 */
export function useDaysRemaining(
  dateString: string | null | undefined,
): DaysRemainingResult {
  const referenceMs = useRenderNowMs({ refreshMs: 60_000 })

  return useMemo(() => {
    const date = parseStableDateInput(dateString)
    if (!date) {
      return { daysRemaining: null, isPast: false, date: null }
    }

    if (referenceMs == null) {
      return {
        daysRemaining: null,
        isPast: false,
        date,
      }
    }

    const daysRemaining = getDaysRemainingAt(date, referenceMs)

    return {
      daysRemaining,
      // Intentionally mirror getDaysRemainingAt()'s ceil-based whole-day math
      // instead of strict timestamp comparison.
      isPast: (daysRemaining ?? 0) < 0,
      date,
    }
  }, [dateString, referenceMs])
}
